/**
 * REST contract만 공유한다 — api와 gui 사이에 shared 모듈을 두지 않는다(전역 규칙).
 * 그래서 응답 타입을 여기에 다시 적는다. 원본은 `apps/api/src/routes/*.ts`다.
 *
 * 절대 URL을 쓰지 않는다(과업지시서 §10-5) — 항상 `/api/...` 상대 경로다.
 */

import { startDevRequest } from '../dev/devNetwork'

export interface CommonCode {
  code: string
  group: string
  label: string
  parentCode: string | null
  sortOrder: number
  isActive: boolean
  note: string | null
}

export interface Region {
  code: string
  level: string
  name: string
  parentCode: string | null
  centroid: { lat: number; lng: number } | null
}

export interface OrgSummary {
  id: string
  name: string
  orgType: string
  certType: string | null
  foundedYear: number | null
  memberScale: string | null
  intro: string | null
  /** 미션·비전(018). 구글폼 선택 문항이라 비어 있을 수 있다. */
  mission: string | null
  vision: string | null
  homepageUrl: string | null
  logoUrl: string | null
  baseRegionCode: string | null
  /** 서버가 만든 지역 이름 — 화면은 이걸 그대로 그린다(코드로 라벨을 다시 만들지 않는다). */
  baseRegionName: string
  operationStatus: string
  createdAt: string
  publishedCardCount: number
}

export interface OrgDetail extends Omit<OrgSummary, 'publishedCardCount'> {
  solutionCards: {
    id: string
    title: string
    summary: string
    categoryCodes: string[]
    resourceCodes: string[]
    serviceAreaCodes: string[]
  }[]
}

export interface SolutionCard {
  id: string
  org: { id: string; name: string; certType: string | null }
  title: string
  summary: string
  problemSolved: string
  body: string
  categoryCodes: string[]
  targetCodes: string[]
  resourceCodes: string[]
  serviceAreaCodes: string[]
  serviceDongCodes: string[] | null
  costType: string
  accessCondition: string | null
  pilotAvailable: string | null
  capacityNote: string | null
  leadTimeDays: number | null
  impactNote: string | null
  tags: string[] | null
  verifiedAt: string | null
  createdAt: string
}

export interface ProblemCard {
  id: string
  title: string
  /**
   * 공개용 비식별 요약(017). AI가 기본 입력하고 관리자가 덮어쓴다(08-31).
   * null이면 아직 아무도 쓰지 않은 것이다.
   */
  summary: string | null
  /**
   * 시민이 쓴 원문(개정판이 있으면 최신본). **상세 조회에만 온다** — 목록 item에는 키가 없다.
   * null은 원문 행이 없는 카드(구글폼 이관분).
   *
   * **한시적이다**(08-31 개방 · 이티랩 결정 대기) — 닫히면 응답에서 키가 사라진다.
   * 그래서 화면은 이 값이 없어도 `summary`로 성립해야 한다(옵셔널인 이유).
   */
  rawText?: string | null
  /**
   * AI 추출 키워드(041). **상세 조회에만 온다** — 목록 item에는 키가 없다.
   * AI가 원문에서 뽑고 관리자가 확정·수정하는 표시용 낱말이고, 최대 5개다.
   * 코드값이 아니라 필터·매칭에 쓰지 않는다. 빈 배열이면 그 블록을 그리지 않는다.
   */
  keywords?: string[]
  categoryCodes: string[] | null
  targetCodes: string[]
  neededResourceCodes: string[] | null
  region: { code: string; name: string }
  urgency: string
  impactScope: string | null
  barrierNote: string | null
  neededResourceNote: string | null
  desiredPeriod: string | null
  createdAt: string
}

/**
 * 게시판(`notice`·`org_visit` — 라벨은 015 기준 `소식`·`이야기`).
 * 목록은 본문 대신 `excerpt`만 온다 — 서버가 줄바꿈을 접어 120자로 자른다.
 * 작성자 이름은 **공개 응답에 없다**(관리자 실명을 공개 화면에 남기지 않는다 — api `post.ts`).
 */
export interface PostSummary {
  id: string
  board: { key: string; label: string }
  title: string
  excerpt: string
  org: { id: string; name: string | null } | null
  viewCount: number
  publishedAt: string | null
}

export interface PostDetail {
  id: string
  board: { key: string; label: string }
  title: string
  /** plain text다. 화면은 줄바꿈만 보존해 그린다(리치 텍스트 미채택 — 개발 플랜 §6-1). */
  body: string
  org: { id: string; name: string | null } | null
  images: { id: string; url: string; altText: string | null }[]
  viewCount: number
  publishedAt: string | null
}

/**
 * 해봄지도 집계 — `GET /api/map/layers` 응답. 원본은 `apps/api/src/routes/map.ts`다.
 *
 * `available:false`인 레이어는 **0건이 아니라 아직 데이터를 저장하지 않는 레이어**다.
 * 화면은 이 둘을 반드시 다르게 그린다.
 */
export interface MapLayer {
  key: string
  label: string
  available: boolean
  total: number
  values: {
    regionCode: string
    count: number
    /** 미충족 레이어만 사유별 내역을 함께 준다. */
    breakdown?: { code: string; count: number }[]
  }[]
  note: string | null
}

export interface MapLayers {
  regions: { code: string; name: string; centroid: { lat: number; lng: number } | null }[]
  layers: MapLayer[]
}

/**
 * 등록 화면이 지켜야 할 갯수 상한(030) — 관리자 설정에서 온다.
 * 숫자를 화면에 다시 박으면 관리자가 바꾼 값과 갈린다(그러려고 설정으로 옮겼다).
 */
export interface Limits {
  categoryMax: number
  resourceSuggestMax: number
  solutionCardPerOrg: number
}

export interface Paged<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

/**
 * 등록 충분성 게이트가 되돌려주는 사유(422 `insufficient` — CLAUDE.md §4-11).
 * **`message`는 시민에게 그대로 보여 주는 문장**이고, `code`는 계약(OpenAPI enum)이라
 * 화면이 분기에 쓸 수 있다. 문구를 화면에서 다시 짓지 않는다 — 서버가 정본이다.
 */
export interface InsufficientReason {
  code: string
  message: string
}

/**
 * API 오류. 400은 필드별 사유가 `detail`에, **422는 `reasons`에** 온다 —
 * 모양이 다르므로 둘 다 들고 있어야 화면이 사유를 잃지 않는다(`reasons`를 안 읽으면
 * "잠시 후 다시 시도해 주세요"만 뜨고 시민은 무엇을 고쳐야 할지 모른다).
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string[]
  readonly reasons: InsufficientReason[]

  constructor(
    status: number,
    code: string,
    detail: string[],
    options?: { reasons?: InsufficientReason[]; cause?: unknown },
  ) {
    const reasons = options?.reasons ?? []
    super(detail[0] ?? reasons[0]?.message ?? code, options)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
    this.reasons = reasons
  }
}

function isErrorBody(v: unknown): v is { error: string; detail?: unknown; reasons?: unknown } {
  return typeof v === 'object' && v !== null && typeof (v as { error?: unknown }).error === 'string'
}

/** 모양이 어긋난 항목은 버린다 — 오류 화면에서 `undefined`를 그리지 않는다. */
function readReasons(raw: unknown): InsufficientReason[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item: unknown) => {
    const code = (item as { code?: unknown })?.code
    const message = (item as { message?: unknown })?.message
    return typeof code === 'string' && typeof message === 'string' ? [{ code, message }] : []
  })
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // dev 오버레이가 켜진 브라우저에서만 실제로 기록한다(꺼져 있으면 빈 함수다).
  const done = startDevRequest(init?.method ?? 'GET', path)
  let res: Response
  try {
    res = await fetch(path, {
      ...init,
      headers: { 'content-type': 'application/json', ...init?.headers },
    })
  } catch (cause) {
    // 네트워크 실패를 조용히 삼키지 않는다 — 화면이 빈 목록으로 오해하면 안 된다.
    done(0, 'network_error')
    throw new ApiError(
      0,
      'network_error',
      ['서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.'],
      { cause },
    )
  }

  const text = await res.text()
  const body: unknown = text.length > 0 ? JSON.parse(text) : null

  if (!res.ok) {
    const code = isErrorBody(body) ? body.error : 'unknown_error'
    const rawDetail = isErrorBody(body) ? body.detail : undefined
    const detail = Array.isArray(rawDetail) ? rawDetail.map(String) : []
    const reasons = readReasons(isErrorBody(body) ? body.reasons : undefined)
    done(res.status, code)
    throw new ApiError(res.status, code, detail, { reasons })
  }

  done(res.status, null)
  return body as T
}

const qs = (params: Record<string, string | number | undefined>): string => {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s.length > 0 ? `?${s}` : ''
}

export const api = {
  commonCodes: (group?: string) =>
    request<{ items: CommonCode[] }>(`/api/common-codes${qs({ group })}`),

  regions: () => request<{ items: Region[] }>('/api/regions'),

  limits: () => request<{ limits: Limits }>('/api/settings/limits'),

  mapLayers: () => request<MapLayers>('/api/map/layers'),

  orgs: () => request<{ items: OrgSummary[] }>('/api/orgs'),

  org: (id: string) => request<OrgDetail>(`/api/orgs/${id}`),

  solutionCards: (params: {
    category?: string
    target?: string
    resource?: string
    serviceArea?: string
    q?: string
    limit?: number
    offset?: number
  }) => request<Paged<SolutionCard>>(`/api/solution-cards${qs(params)}`),

  solutionCard: (id: string) => request<SolutionCard>(`/api/solution-cards/${id}`),

  problemCards: (params: {
    category?: string
    target?: string
    region?: string
    urgency?: string
    limit?: number
    offset?: number
  }) => request<Paged<ProblemCard>>(`/api/problem-cards${qs(params)}`),

  problemCard: (id: string) => request<ProblemCard>(`/api/problem-cards/${id}`),

  posts: (params: { board?: string; q?: string; limit?: number; offset?: number }) =>
    request<Paged<PostSummary>>(`/api/posts${qs(params)}`),

  post: (id: string) => request<PostDetail>(`/api/posts/${id}`),

  /**
   * 시민문제 접수. **`editPath`는 이 응답에만 실린다** — 재발급 경로가 없으므로
   * 화면이 받는 즉시 시민에게 보여 줘야 한다(개발플랜 §2 항목 3).
   */
  createProblemCard: (body: CreateProblemCardBody) =>
    request<{ id: string; title: string; isPublished: boolean; editPath: string }>(
      '/api/problem-cards',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * 시민 수정 — 등록 201 응답의 `editPath`에 실린 토큰이 유일한 본인 확인 수단이다.
   * 토큰이 틀리거나 카드가 없으면 **똑같이 404**다(갈라 답하면 카드 존재가 열거된다).
   */
  problemCardEdit: (id: string, token: string) =>
    request<EditableProblemCard>(`/api/problem-cards/${id}/edit${qs({ t: token })}`),

  updateProblemCard: (id: string, token: string, body: UpdateProblemCardBody) =>
    request<{ id: string; title: string; isPublished: boolean; reviewStatus: string }>(
      `/api/problem-cards/${id}/edit${qs({ t: token })}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
}

/**
 * 주소 — **중첩 객체로 보낸다**(FE 2026-08-27 계약). 서버가 `liftNestedAddress`로 평면
 * `regionCode`·`addressText`로 펴서 검증하고, 온 값 자체는 입력 원본으로 남긴다
 * (`problem_card_raw.raw_input_json`). 정본은
 * `docs/10_설계/주소검색-행정동자동기록-2026-08-25.md` §7-5다.
 *
 * **좌표는 없다.** 지도 미리보기에만 쓰고 보내지 않는다(설계 원칙 7 — 동 단위 익명화).
 */
export interface AddressPayload {
  /** 주소 글자(도로명이 있으면 도로명). */
  text: string
  /** 행정동코드 10자리 — 카카오 `h_code` ≡ 우리 `region.code`. 이것이 `regionCode`가 된다. */
  adminCode: string
  /** 코드↔이름 교차검증용. 표시 정본은 서버의 `region.name_ko`다. */
  adminName?: string
  legalCode?: string
  legalName?: string
  postalCode?: string
}

/**
 * 시민문제 접수 — **연락처·동의가 필수다**(08-21 C-8. 옛 설계서 C-01 「개인정보 0개」를
 * 뒤집었다). 이름 + 휴대폰·이메일 **택1** + `privacy`·`disclose` 동의 **둘 다**가 없으면
 * 서버가 400을 낸다.
 */
export interface CreateProblemCardBody {
  rawText: string
  targetCodes: string[]
  /** 주소. 서버가 여기서 `regionCode`를 편다 — 평면 키를 따로 보내지 않는다. */
  address: AddressPayload
  /** 이름(필수). */
  contactName: string
  /** 닿을 수단 — 둘 중 **하나**만 보낸다. */
  contactPhone?: string
  contactEmail?: string
  /** 동의(필수) — `REQUIRED_CITIZEN_CONSENTS` 전부를 담아야 한다. */
  consents: string[]
  title?: string
  categoryCodes?: string[]
  neededResourceCodes?: string[]
  urgency?: 'low' | 'mid' | 'high'
  impactScope?: 'personal' | 'neighbor' | 'dong_wide'
  desiredPeriod?: 'immediate' | 'within_3m' | 'within_6m' | 'anytime'
  barrierNote?: string
  neededResourceNote?: string
  unknownFields?: string[]
  /** 익명 세션 식별자(설계서 D-03). 개인정보가 아니다 — `lib/session.ts` 참조. */
  sessionId?: string
}

/**
 * 수정 화면이 받는 등록 당시 값 — **등록 폼을 그대로 채우기 위한 것**이다(09-04 합의).
 *
 * `PUT …/edit`이 **전체 교체**라, 여기 안 실려 온 축을 화면이 빈 칸으로 그리면 재제출 때
 * 그 값이 지워진다. 그래서 연락처·동의까지 평문으로 온다 — 이 응답의 방어선은 **토큰 하나**다.
 */
export interface EditableProblemCard {
  id: string
  /** 파생 제목은 `null`로 온다 — 시민이 "내가 쓴 제목"으로 오인하지 않게. */
  title: string | null
  rawText: string
  addressText: string | null
  /** 접수 때 온 주소 객체 그대로(좌표는 없다 — 보내지 않았으므로). */
  address: AddressPayload | null
  categoryCodes: string[] | null
  targetCodes: string[]
  targetEtcText: string | null
  neededResourceCodes: string[] | null
  regionCode: string
  urgency: string
  impactScope: string | null
  barrierNote: string | null
  neededResourceNote: string | null
  pilotConditionNote: string | null
  desiredPeriod: string | null
  reviewStatus: string
  isPublished: boolean
  hasContact: boolean
  /** 「잘 모르겠어요」로 표시했던 축 — 안 되살리면 재제출 때 표시가 사라진다. */
  unknownFields: string[]
  contactName: string | null
  contactPhone: string | null
  contactEmail: string | null
  consents: string[]
  rawTextEditable: boolean
}

/**
 * 시민 수정 — **전체 교체 의미론**이다. 안 보낸 선택 축은 지워진다고 보고 폼이 전부 싣는다.
 *
 * 예외는 연락처·동의다: **하나라도 보내면 연락처 블록 전체를 교체**하고, 아예 안 보내면
 * 종전 값이 남는다. `consents`는 철회 경로가 아니다(보내면 필수 2종을 다시 확인할 뿐).
 */
export interface UpdateProblemCardBody {
  rawText: string
  targetCodes: string[]
  regionCode: string
  /** 주소 객체 — 서버가 `regionCode`·`addressText`로 편다(등록과 같은 계약). */
  address?: AddressPayload
  title?: string
  targetEtcText?: string | null
  categoryCodes?: string[]
  neededResourceCodes?: string[]
  urgency?: 'low' | 'mid' | 'high'
  impactScope?: 'personal' | 'neighbor' | 'dong_wide' | null
  desiredPeriod?: 'immediate' | 'within_3m' | 'within_6m' | 'anytime' | null
  barrierNote?: string | null
  neededResourceNote?: string | null
  pilotConditionNote?: string | null
  unknownFields?: string[]
  contactName?: string
  contactPhone?: string
  contactEmail?: string
  consents?: string[]
  /** 게이트 재시도 횟수 — 3회차부터 탈출구가 열린다(원문을 고쳤을 때만 게이트를 지난다). */
  attempt?: number
}
